/**
 * 
 * @Description: 
 * @Author: richen
 * @Date: 2025-04-04 12:21:48
 * @LastEditTime: 2025-04-06 12:59:31
 * @License: BSD (3-Clause)
 * @Copyright (c): <richenlin(at)gmail.com>
 */
import { IOCContainer } from "koatty_container";
import { AppEvent, Koatty, KoattyContext, KoattyNext } from "koatty_core";
import { Helper } from "koatty_lib";
import { SpanManager } from '../opentelemetry/spanManager';
import { SpanStatusCode } from '@opentelemetry/api';
import { performance } from 'node:perf_hooks';
import { HandlerFactory } from '../handler/factory';
import { ProtocolType } from '../handler/base';
import { asyncLocalStorage, createAsyncResource, wrapEmitter } from './wrap';
import { extensionOptions, TraceOptions } from "./itrace";
import { initSDK, startTracer } from "../opentelemetry/sdk";
import { TopologyAnalyzer } from "../opentelemetry/topology";
import { getRequestId, getTraceId } from '../utils/utils';
import { collectRequestMetrics, initPrometheusExporter } from '../opentelemetry/prometheus';
import { DefaultLogger as Logger } from "koatty_logger";
import { initializeRequestProperties } from '../utils/contextInit';

/**
 * Default retry condition - only retry transient network errors
 */
const defaultRetryCondition = (error: Error): boolean => {
  const transientCodes = ['ECONNRESET', 'ECONNREFUSED', 'ETIMEDOUT', 'EPIPE'];
  const code = (error as any).code;
  return transientCodes.includes(code) || 
    (error.message && /timeout|ECONNRESET|socket hang up/i.test(error.message));
};

/** 
 * defaultOptions
 */
const defaultOptions = {
  // response timeout in milliseconds
  timeout: 10000,
  // request id header name
  requestIdHeaderName: 'X-Request-Id',
  // request id name
  requestIdName: "requestId",
  // id factory function
  idFactory: getTraceId,
  // encoding
  encoding: 'utf-8',
  // Whether to enable trace (default: false)
  enableTrace: false,
  // asynchooks enabled
  asyncHooks: false,
  /**
   * Metrics configuration
   */
  metricsConf: {
    /**
     * Metrics reporter function
     */
    // reporter: (metrics: {
    //   duration: number,
    //   status: number,
    //   path: string,
    //   attributes: Record<string, any>,
    // }) => void,
    /**
     * Default attributes for metrics
     */
    defaultAttributes: {},
    /**
     * Prometheus metrics endpoint (production only)
     */
    metricsEndpoint: '/metrics',
    /**
     * Metrics report interval in milliseconds (default: 5000)
     */
    reportInterval: 5000,
    /**
     * Prometheus metrics port (default: 9464)
     */
    metricsPort: 9464,
  },

  /**
   * OpenTelemetry configuration
   */
  opentelemetryConf: {
    /**
     * OTLP endpoint URL
     */
    endpoint: "http://localhost:4318/v1/traces",
    /**
     * Whether to enable topology analysis (default: same as enableTrace)
     * 
     */
    enableTopology: false,
    /**
     * OTLP headers
     */
    headers: {},
    /**
     * Resource attributes
     */
    resourceAttributes: {},
    /**
     * Instrumentations to enable
     */
    instrumentations: [] as any,
    /**
     * Exporter timeout in milliseconds
     */
    timeout: 10000,
    /**
     * Maximum lifetime for a span in milliseconds
     */
    spanTimeout: 30000,
    /**
     * Maximum number of active spans in memory (default: 1000)
     */
    maxActiveSpans: 1000,
    /**
     * Request attributes to be added to the span
     */
    // spanAttributes: (ctx: KoattyContext) => Record<string, any>,

    /**
     * Sampling rate (0.0 - 1.0)
     */
    samplingRate: 1.0,
    /**
     * Maximum number of spans in batch queue
     */
    batchMaxQueueSize: 2048,
    /**
     * Maximum number of spans to export in one batch
     */
    batchMaxExportSize: 512,
    /**
     * Delay between batch exports in milliseconds
     */
    batchDelayMillis: 5000,
    /**
     * Timeout for batch export in milliseconds
     */
    batchExportTimeout: 30000,
  },
  /**
   * Retry configuration
   */
  retryConf: {
    /**
     * Whether to enable retry mechanism (default: false)
     */
    enabled: false,
    /**
     * Max retry count when error occurs (default: 3)
     */
    count: 3,
    /**
     * Retry interval in milliseconds (default: 1000)
     */
    interval: 1000,
    /**
     * Custom function to determine if error should be retried
     */
    // conditions: (error: any) => boolean,
  },
};

/**
 * Trace middleware for Koatty framework that provides request tracing, topology analysis,
 * and request lifecycle management capabilities.
 * 
 * @param {TraceOptions} options - Configuration options for the trace middleware
 * @param {Koatty} app - Koatty application instance
 * @returns {Function} Middleware function that handles request tracing and lifecycle
 * 
 * Features:
 * - Request tracing with OpenTelemetry
 * - Request ID generation and propagation
 * - Service topology analysis
 * - Request lifecycle management
 * - Server shutdown handling
 * - Async hooks support for request context
 * 
 * @export
 */
export function Trace(options: TraceOptions, app: Koatty) {
  options = { ...defaultOptions, ...options };

  // ============================================
  // 应用级别初始化（在 middleware 创建时完成）
  // ============================================
  let spanManager: SpanManager | undefined;
  let sdk: any;
  let metricsProvider: any;

  // 1. 初始化 Prometheus Metrics
  if (options.enableTrace && options.metricsConf?.metricsEndpoint) {
    try {
      metricsProvider = initPrometheusExporter(app, options);
      if (metricsProvider) {
        Logger.Debug('[Trace] Prometheus metrics initialized');
      }
    } catch (error) {
      Logger.Error('[Trace] Failed to initialize Prometheus:', error);
    }
  }

  // 2. 初始化 SpanManager
  if (options.enableTrace) {
    try {
      spanManager = new SpanManager(options);
      Helper.define(app, 'spanManager', spanManager);
      Logger.Debug('[Trace] Span Manager initialized');
    } catch (error) {
      Logger.Error('[Trace] Failed to initialize SpanManager:', error);
    }
  }

  // 3. 初始化 OpenTelemetry SDK
  if (options.enableTrace) {
    try {
      sdk = initSDK(app, options);
      Helper.define(app, 'otelSDK', sdk);
      Logger.Debug('[Trace] OpenTelemetry SDK initialized');

      // 4. 在 appStart 事件启动 Tracer
      app.once(AppEvent.appStart, async () => {
        try {
          await startTracer(sdk, app, options);
          const tracer = sdk.getTracer();
          Helper.define(app, 'otelTracer', tracer);
          Logger.Debug('[Trace] Tracer started');
        } catch (error) {
          Logger.Error('[Trace] Failed to start tracer:', error);
        }
      });
    } catch (error) {
      Logger.Error('[Trace] Failed to initialize OpenTelemetry SDK:', error);
    }
  }

  // 5. 监听 appStop 事件清理资源
  app.once(AppEvent.appStop, async () => {
    Logger.Debug('[Trace] Shutting down tracing infrastructure...');
    
    try {
      // 清理 SpanManager
      if (spanManager) {
        spanManager.destroy();
        Logger.Debug('[Trace] Span Manager destroyed');
      }

      // 关闭 OpenTelemetry SDK
      if (sdk) {
        await sdk.shutdown();
        Logger.Debug('[Trace] OpenTelemetry SDK shutdown');
      }

      // 关闭 Prometheus Metrics
      if (metricsProvider) {
        // MeterProvider shutdown if needed
        Logger.Debug('[Trace] Metrics provider shutdown');
      }

      Logger.Debug('[Trace] Tracing infrastructure shutdown completed');
    } catch (error) {
      Logger.Error('[Trace] Error shutting down tracing:', error);
    }
  });

  // 创建静态配置对象（只创建一次）
  const staticExt = {
    debug: app.appDebug,
    timeout: options.timeout,
    encoding: options.encoding,
    get globalErrorHandler() {
      const handler = IOCContainer.getClass("ExceptionHandler", "COMPONENT");
      Object.defineProperty(this, 'globalErrorHandler', { value: handler });
      return handler;
    },
  };

  return async (ctx: KoattyContext, next: KoattyNext) => {
    // Handle server shutdown case first
    // Support both single server and array of servers
    // Match server by protocol from ctx.protocol
    const requestProtocol = (ctx?.protocol || "http").toLowerCase();
    let targetServer: any;
    
    if (Array.isArray(app.server)) {
      // Find server matching the request protocol
      targetServer = app.server.find((server: any) => {
        const serverProtocol = server?.protocol?.toLowerCase();
        // Protocol mapping: https/http2/http3 -> http, wss -> ws
        if (requestProtocol === 'https' || requestProtocol === 'http2' || requestProtocol === 'http3') {
          return serverProtocol === 'http' || serverProtocol === 'https' || 
                 serverProtocol === 'http2' || serverProtocol === 'http3';
        }
        if (requestProtocol === 'wss') {
          return serverProtocol === 'ws' || serverProtocol === 'wss';
        }
        return serverProtocol === requestProtocol;
      });
    } else {
      targetServer = app.server;
    }
    
    if (targetServer?.status === 503) {
      ctx.status = 503;
      ctx.set('Connection', 'close');
      ctx.body = 'Server is in the process of shutting down';
      return;
    }

    // Initialize common request properties (prevent redefinition in multi-protocol scenarios)
    const requestId = getRequestId(ctx, options);
    initializeRequestProperties(ctx, requestId);

    // Create span if tracing is enabled and infrastructure is ready
    // 使用闭包中初始化的资源
    if (options.enableTrace && spanManager) {
      const tracer = (app as any).otelTracer;
      if (tracer) {
        const serviceName = app.name || "unknownKoattyProject";
        spanManager.createSpan(tracer, ctx, serviceName);
        // Span is ended in handleRequest finally block after request completion
      }
    }

    // Record topology if enabled
    if (options.opentelemetryConf?.enableTopology ?? options.enableTrace) {
      const topology = TopologyAnalyzer.getInstance();
      const serviceName = Array.isArray(ctx.headers['service'])
        ? ctx.headers['service'][0]
        : ctx.headers['service'] || 'unknown';
      topology.recordServiceDependency(app.name, serviceName);
    }

    // 使用原型链继承静态配置，只添加动态属性
    const ext = Object.create(staticExt) as extensionOptions;
    ext.requestId = requestId;
    ext.terminated = false;
    ext.spanManager = spanManager;

    // Handle async hooks if enabled
    if (options.asyncHooks && (ctx.req || ctx.res)) {
      const asyncResource = createAsyncResource();
      return asyncLocalStorage.run(requestId, () => {
        if (ctx.req) wrapEmitter(ctx.req, asyncResource);
        if (ctx.res) wrapEmitter(ctx.res, asyncResource);
        return handleRequest(ctx, next, options, ext);
      });
    }

    return handleRequest(ctx, next, options, ext);
  };
}

/**
 * Handle HTTP request with tracing and metrics reporting
 * 
 * @param ctx - Koatty context object
 * @param next - Next middleware function
 * @param options - Trace configuration options
 * @param ext - Extension options containing span information
 * @returns Promise with the request handling result
 * 
 * @description
 * Wraps request handling with tracing functionality:
 * - Measures request duration
 * - Reports metrics if configured
 * - Manages span lifecycle
 * - Handles request response
 */
async function handleRequest(
  ctx: KoattyContext,
  next: KoattyNext,
  options: TraceOptions,
  ext: extensionOptions
) {
  const startTime = performance.now();
  let result;
  const retryConf = options.retryConf || { enabled: false };

  try {
    if (retryConf.enabled) {
      const maxRetries = retryConf.count || 3;
      const interval = retryConf.interval || 1000;

      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          result = await respWarper(ctx, next, options, ext);
          break;
        } catch (error) {
          // Check if error should be retried
          const shouldRetry = retryConf.conditions
            ? retryConf.conditions(error)
            : defaultRetryCondition(error);

          if (!shouldRetry || attempt === maxRetries) {
            throw error;
          }

          // Wait before next retry
          if (interval > 0) {
            await new Promise(resolve => setTimeout(resolve, interval));
          }
        }
      }
    } else {
      result = await respWarper(ctx, next, options, ext);
    }
  } finally {
    // Calculate request duration
    const duration = performance.now() - startTime;

    // Collect request metrics using the new metrics collector
    collectRequestMetrics(ctx, duration);

    // Legacy metrics reporter support (for backward compatibility)
    const metricsConf = options.metricsConf || {};
    if (metricsConf.reporter) {
      try {
        metricsConf.reporter({
          duration,
          status: ctx.status || 200,
          path: ctx.path,
          attributes: {
            ...(metricsConf.defaultAttributes || {}),
            requestId: ctx.requestId,
            method: ctx.method,
            protocol: ctx.protocol
          }
        });
      } catch (error) {
        // Don't let metrics reporting errors affect the request
        Logger.Warn('Metrics reporter error:', error);
      }
    }

    // End span on request completion to ensure accurate durations and prevent memory leaks
    if (ext.spanManager) {
      const span = ext.spanManager.getSpan(ctx);
      if (span) {
        try {
          span.setStatus({ code: ctx.status >= 400 ? SpanStatusCode.ERROR : SpanStatusCode.OK });
          ext.spanManager.endSpan(ctx);
        } catch (error) {
          Logger.Warn('Error ending span:', error);
        }
      }
    }
  }

  return result;
}

/**
 * Wraps the response handling process with trace functionality.
 * 
 * @param ctx - Koatty context object
 * @param next - Next middleware function
 * @param options - Trace configuration options
 * @param ext - Extension options for tracing
 * @returns Promise that resolves after handling the request
 * @throws Rethrows any errors that occur during handling
 */
async function respWarper(
  ctx: KoattyContext,
  next: KoattyNext,
  options: TraceOptions,
  ext: extensionOptions
) {
  if (options.requestIdName && ctx.setMetaData) {
    ctx.setMetaData(options.requestIdName, ctx.requestId);
  }

  const protocol = (ctx?.protocol || "http").toLowerCase();
  if (protocol === "grpc" || protocol === "ws" || protocol === "wss") {
    ctx.respond = false;
  }

  if (options.requestIdHeaderName) {
    ctx.set(options.requestIdHeaderName, ctx.requestId);
  }

  if (ctx.rpc?.call?.metadata && options.requestIdName) {
    ctx.rpc.call.metadata.set(options.requestIdName, ctx.requestId);
  }

  const handler = HandlerFactory.getHandler(protocol as ProtocolType);
  return handler.handle(ctx, next, ext);
}
